import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import DashboardLayout from "../components/DashboardLayout";
import { auth, db } from "../firebase";
import {
  collection,
  addDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  doc,
  getDoc,
  serverTimestamp,
  query,
  orderBy,
  where,
} from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import * as XLSX from "xlsx";
import {
  formatDate,
  formatDateForInput,
  parseDateString,
} from "../utils/dateUtils";
import { createNotification } from "../utils/notifications";
import { createUserAccount, generateEmail } from "../utils/createUserAccount";

function Candidates() {
  const { t } = useTranslation();
  const [candidates, setCandidates] = useState([]);
  const [instructors, setInstructors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [candidateToDelete, setCandidateToDelete] = useState(null);
  const [viewingCandidate, setViewingCandidate] = useState(null);
  const [editingCandidate, setEditingCandidate] = useState(null);
  const [toast, setToast] = useState({ show: false, message: "", type: "" });
  const [userRole, setUserRole] = useState(null);

  // Check if user is admin
  const isAdmin = userRole === "admin";

  const [formData, setFormData] = useState({
    candidateId: "",
    firstName: "",
    lastName: "",
    phonePrefix: "+383", // Default phone prefix
    phoneNumber: "",
    email: "",
    username: "",
    personalNumber: "",
    password: "",
    birthday: "",
    gender: "",
    address: "",
    parentName: "",
    category: "",
    status: "active",
    assignedInstructorId: "", // Store instructor ID instead of name
    startDate: "",
    theoryTestDate: "",
    practicalTestDate: "",
    totalHours: "",
    completedHours: "",
    bonusHours: "",
    completedBonusHours: "",
    totalAmount: "",
    paidAmount: "",
    notes: "",
    healthCertificateDate: "",
    redCrossDate: "",
    applicationAutoShkolla: false,
    literature: false,
    transmissionType: "",
    theoryTestStatus: "notCompleted",
    practicalTestStatus: "notCompleted",
    candidateRating: "",
  });

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  // Search and Filter states
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [filterInstructor, setFilterInstructor] = useState("");
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo, setFilterDateTo] = useState("");
  const [sortBy, setSortBy] = useState("date-newest"); // date-newest, date-oldest, name-az, name-za
  const [selectedYear, setSelectedYear] = useState("all");
  const [selectedMonth, setSelectedMonth] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 30;

  // Fetch candidates from Firestore
  const fetchCandidates = async () => {
    try {
      const q = query(
        collection(db, "candidates"),
        orderBy("createdAt", "desc")
      );
      const querySnapshot = await getDocs(q);
      const candidatesData = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setCandidates(candidatesData);
    } catch (error) {
      console.error("Error fetching candidates:", error);
      showToast(t("candidates.toast.fetchError"), "error");
    } finally {
      setLoading(false);
    }
  };

  // Fetch instructors from Firestore
  const fetchInstructors = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, "instructors"));
      const instructorsData = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setInstructors(instructorsData);
    } catch (error) {
      console.error("Error fetching instructors:", error);
      showToast(t("candidates.toast.fetchInstructorsError"), "error");
    }
  };

  useEffect(() => {
    const loadData = async () => {
      await fetchCandidates();
      await fetchInstructors();
    };
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch user role and entityId
  const [userEntityId, setUserEntityId] = useState(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          const userDoc = await getDoc(doc(db, "users", user.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            setUserRole(userData.role || null);
            setUserEntityId(userData.entityId || null);
          }
        } catch (error) {
          console.error("Error fetching user role:", error);
        }
      } else {
        setUserRole(null);
        setUserEntityId(null);
      }
    });

    return () => unsubscribe();
  }, []);

  // Auto-generate password from initials + last 4 digits of ID
  const generatePassword = (firstName, lastName, personalNumber) => {
    if (!firstName || !lastName || !personalNumber) return "";

    const firstInitial = firstName.charAt(0).toUpperCase();
    const lastInitial = lastName.charAt(0).toUpperCase();
    const last4Digits = personalNumber.slice(-4);

    return `${firstInitial}${lastInitial}${last4Digits}`;
  };

  // Update password when firstName, lastName, or personalNumber changes
  useEffect(() => {
    const newPassword = generatePassword(
      formData.firstName,
      formData.lastName,
      formData.personalNumber
    );
    if (newPassword && newPassword !== formData.password) {
      setFormData((prev) => ({ ...prev, password: newPassword }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.firstName, formData.lastName, formData.personalNumber]);

  // Validation
  const validateField = (name, value) => {
    let error = "";

    // Required fields
    const requiredFields = [
      "firstName",
      "lastName",
      "phonePrefix",
      "phoneNumber",
      "username",
      "birthday",
      "gender",
      "startDate",
      "category",
      "totalAmount",
      "status",
      "transmissionType",
    ];

    if (requiredFields.includes(name) && !value) {
      error = t("candidates.validation.required");
    }

    // Candidate ID validation
    if (name === "candidateId" && value && value.length < 3) {
      error = t("candidates.validation.candidateIdLength");
    }

    // Email validation
    if (
      name === "email" &&
      value &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
    ) {
      error = t("candidates.validation.emailInvalid");
    }

    // Phone prefix validation
    if (name === "phonePrefix" && value && !/^\+?\d{1,4}$/.test(value)) {
      error = t("candidates.validation.phonePrefixInvalid");
    }

    // Phone number validation (without prefix)
    if (name === "phoneNumber" && value && !/^[\d\s\-()]+$/.test(value)) {
      error = t("candidates.validation.phoneInvalid");
    }

    // Combined phone validation: if phoneNumber exists, phonePrefix should exist
    if (name === "phoneNumber" && value && !formData.phonePrefix) {
      error = t("candidates.validation.phonePrefixRequired");
    }

    // Combined phone validation: if phonePrefix exists, phoneNumber should exist
    if (name === "phonePrefix" && value && !formData.phoneNumber) {
      error = t("candidates.validation.phoneNumberRequired");
    }

    // Personal number validation (10 digits)
    if (
      name === "personalNumber" &&
      value &&
      value.replace(/\D/g, "").length !== 10
    ) {
      error = t("candidates.validation.personalNumberLength");
    }

    // Username validation
    if (name === "username" && value && value.length < 3) {
      error = t("candidates.validation.usernameLength");
    }

    // Date validation (DD/MM/YYYY format)
    const dateFields = [
      "birthday",
      "startDate",
      "theoryTestDate",
      "practicalTestDate",
      "healthCertificateDate",
      "redCrossDate",
    ];
    if (dateFields.includes(name) && value) {
      // Check if date is in DD/MM/YYYY format
      const datePattern = /^(\d{2})\/(\d{2})\/(\d{4})$/;
      if (!datePattern.test(value)) {
        // Only show error if it's a required field or if it's partially filled
        if ((name === "birthday" || name === "startDate") && value.length > 0) {
          error = t("candidates.validation.dateInvalid");
        }
      } else {
        // Validate that it's a valid date
        const parsed = parseDateString(value);
        if (!parsed) {
          if (name === "birthday" || name === "startDate") {
            error = t("candidates.validation.dateInvalid");
          }
        }
      }
    }

    return error;
  };

  // Handle input change
  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (touched[name]) {
      const error = validateField(name, value);
      setErrors((prev) => ({
        ...prev,
        [name]: error,
      }));
    }
  };

  // Handle input blur
  const handleBlur = (e) => {
    const { name, value } = e.target;

    setTouched((prev) => ({
      ...prev,
      [name]: true,
    }));

    const error = validateField(name, value);
    setErrors((prev) => ({
      ...prev,
      [name]: error,
    }));
  };

  // Format date as user types (DD/MM/YYYY)
  const formatDateInput = (value) => {
    // Remove all non-digit characters
    const digits = value.replace(/\D/g, "");

    // Limit to 8 digits (DDMMYYYY)
    const limited = digits.slice(0, 8);

    // Format: DD/MM/YYYY
    if (limited.length <= 2) {
      return limited;
    } else if (limited.length <= 4) {
      return `${limited.slice(0, 2)}/${limited.slice(2)}`;
    } else {
      return `${limited.slice(0, 2)}/${limited.slice(2, 4)}/${limited.slice(
        4
      )}`;
    }
  };

  // Handle date input change with auto-formatting
  const handleDateChange = (e) => {
    const { name, value } = e.target;
    const formatted = formatDateInput(value);

    setFormData((prev) => ({
      ...prev,
      [name]: formatted,
    }));

    if (touched[name]) {
      const error = validateField(name, formatted);
      setErrors((prev) => ({
        ...prev,
        [name]: error,
      }));
    }
  };

  // Convert DD/MM/YYYY to Date object for saving
  const convertDateToISO = (dateString) => {
    if (!dateString) return "";
    const parsed = parseDateString(dateString);
    if (parsed) {
      return parsed.toISOString();
    }
    return dateString; // Return as-is if can't parse
  };

  // Validate all fields
  const validateForm = () => {
    const newErrors = {};
    const requiredFields = [
      "firstName",
      "lastName",
      "phonePrefix",
      "phoneNumber",
      "username",
      "birthday",
      "gender",
      "startDate",
      "category",
      "totalAmount",
      "status",
    ];

    requiredFields.forEach((field) => {
      const error = validateField(field, formData[field]);
      if (error) {
        newErrors[field] = error;
      }
    });

    // Also validate optional fields if they have values
    if (formData.email) {
      const emailError = validateField("email", formData.email);
      if (emailError) newErrors.email = emailError;
    }

    setErrors(newErrors);
    setTouched(
      requiredFields.reduce((acc, field) => ({ ...acc, [field]: true }), {})
    );

    return Object.keys(newErrors).length === 0;
  };

  // Show toast notification
  const showToast = (message, type = "success") => {
    setToast({ show: true, message, type });
    setTimeout(() => {
      setToast({ show: false, message: "", type: "" });
    }, 3000);
  };

  // Helper function to get instructor name by ID
  const getInstructorNameById = (instructorId) => {
    if (!instructorId) return "";
    const instructor = instructors.find((i) => i.id === instructorId);
    return instructor ? `${instructor.firstName} ${instructor.lastName}` : "";
  };

  // Helper function to get instructor ID by name (for backwards compatibility with old data)
  const getInstructorIdByName = (instructorName) => {
    if (!instructorName) return "";
    const instructor = instructors.find(
      (i) => `${i.firstName} ${i.lastName}` === instructorName
    );
    return instructor?.id || "";
  };

  // Helper function to get full phone number (prefix + number)
  const getFullPhoneNumber = (candidate) => {
    const prefix =
      candidate.phonePrefix ||
      candidate.phoneNumber?.match(/^\+?\d{1,4}/)?.[0] ||
      "+383";
    const number = candidate.phoneNumber || "";

    // If phoneNumber already includes prefix, return as is
    if (number.startsWith("+")) {
      let cleaned = number.replace(/\s/g, "");
      // Remove leading 0 after country code (e.g., +383044986926 -> +38344986926)
      if (
        cleaned.startsWith("+383") &&
        cleaned.length > 4 &&
        cleaned[4] === "0"
      ) {
        cleaned = "+383" + cleaned.substring(5);
      }
      // Format: +383 44 986 926
      if (cleaned.startsWith("+383") && cleaned.length > 4) {
        const rest = cleaned.substring(4);
        if (rest.length >= 2) {
          const part1 = rest.substring(0, 2);
          const part2 = rest.length > 2 ? rest.substring(2, 5) : "";
          const part3 = rest.length > 5 ? rest.substring(5) : "";
          return `+383 ${part1}${part2 ? " " + part2 : ""}${
            part3 ? " " + part3 : ""
          }`.trim();
        }
        return `+383 ${rest}`;
      }
      return cleaned;
    }

    // Combine prefix and number, remove spaces
    let fullNumber = `${prefix}${number.replace(/\s/g, "")}`;
    // Remove leading 0 after country code (e.g., 383044986926 -> 38344986926)
    if (
      fullNumber.startsWith("+383") &&
      fullNumber.length > 4 &&
      fullNumber[4] === "0"
    ) {
      fullNumber = "+383" + fullNumber.substring(5);
    }
    // Format: +383 44 986 926
    if (fullNumber.startsWith("+383") && fullNumber.length > 4) {
      const rest = fullNumber.substring(4);
      if (rest.length >= 2) {
        const part1 = rest.substring(0, 2);
        const part2 = rest.length > 2 ? rest.substring(2, 5) : "";
        const part3 = rest.length > 5 ? rest.substring(5) : "";
        return `+383 ${part1}${part2 ? " " + part2 : ""}${
          part3 ? " " + part3 : ""
        }`.trim();
      }
      return `+383 ${rest}`;
    }
    return fullNumber;
  };

  // Helper function to open WhatsApp chat
  const openWhatsApp = (candidate) => {
    const fullNumber = getFullPhoneNumber(candidate);
    // Remove + and spaces
    let whatsappNumber = fullNumber.replace(/\+|\s/g, "");
    // Remove leading 0 after country code (e.g., 383044986926 -> 38344986926)
    if (whatsappNumber.startsWith("383") && whatsappNumber[3] === "0") {
      whatsappNumber = "383" + whatsappNumber.substring(4);
    }
    window.open(`https://wa.me/${whatsappNumber}`, "_blank");
  };

  // Helper function to open Viber chat
  const openViber = (candidate) => {
    const fullNumber = getFullPhoneNumber(candidate);
    // Remove + and spaces
    let viberNumber = fullNumber.replace(/\+|\s/g, "");
    // Remove leading 0 after country code (e.g., 383044986926 -> 38344986926)
    if (viberNumber.startsWith("383") && viberNumber[3] === "0") {
      viberNumber = "383" + viberNumber.substring(4);
    }
    window.open(`viber://chat?number=${viberNumber}`, "_blank");
  };

  // Helper function to get status color for row indicator
  const getStatusColor = (status) => {
    switch (status) {
      case "active":
        return "bg-green-500";
      case "completed":
        return "bg-black";
      case "onProcess":
        return "bg-purple-500";
      case "paused":
        return "bg-yellow-500";
      case "failed":
        return "bg-red-500";
      default:
        return "bg-gray-500";
    }
  };

  // Calculate hours completed with an instructor from lessons
  const calculateHoursWithInstructor = async (candidateId, instructorId) => {
    if (!candidateId || !instructorId) return { normal: 0, bonus: 0, total: 0 };

    try {
      const lessonsSnapshot = await getDocs(
        query(
          collection(db, "lessons"),
          where("candidateId", "==", candidateId),
          where("instructorId", "==", instructorId)
        )
      );

      let normalHours = 0;
      let bonusHours = 0;

      lessonsSnapshot.docs.forEach((lessonDoc) => {
        const lesson = lessonDoc.data();
        const hours = parseFloat(lesson.hours) || 0;
        const hoursType = lesson.hoursType || "normal";

        if (hoursType === "normal") {
          normalHours += hours;
        } else if (hoursType === "bonus") {
          bonusHours += hours;
        } else if (hoursType === "normalAndBonus") {
          // For normalAndBonus, we need to check the candidate's totalHours
          // This is approximate - we'll count it as normal for now
          normalHours += hours;
        }
      });

      return {
        normal: normalHours,
        bonus: bonusHours,
        total: normalHours + bonusHours,
      };
    } catch (error) {
      console.error("Error calculating hours with instructor:", error);
      return { normal: 0, bonus: 0, total: 0 };
    }
  };

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      showToast(t("candidates.toast.fillRequired"), "error");
      return;
    }

    try {
      if (editingCandidate) {
        // Check if instructor is being changed
        const oldInstructorId = editingCandidate.assignedInstructorId || "";
        const newInstructorId = formData.assignedInstructorId || "";

        let instructorHistory = editingCandidate.instructorHistory || [];

        // If instructor changed and there was a previous instructor
        if (
          oldInstructorId &&
          newInstructorId &&
          oldInstructorId !== newInstructorId
        ) {
          // Update all lessons without instructorId to have the old instructorId
          // This ensures old lessons are tracked correctly
          try {
            const allLessonsSnapshot = await getDocs(
              query(
                collection(db, "lessons"),
                where("candidateId", "==", editingCandidate.id)
              )
            );

            const updatePromises = [];
            allLessonsSnapshot.docs.forEach((lessonDoc) => {
              const lesson = lessonDoc.data();
              if (!lesson.instructorId) {
                // Update lesson to have the old instructorId
                updatePromises.push(
                  updateDoc(doc(db, "lessons", lessonDoc.id), {
                    instructorId: oldInstructorId,
                  })
                );
              }
            });

            await Promise.all(updatePromises);
          } catch (error) {
            console.error("Error updating lessons with instructorId:", error);
          }

          // Calculate hours completed with the old instructor
          const hoursWithOldInstructor = await calculateHoursWithInstructor(
            editingCandidate.id,
            oldInstructorId
          );

          // Get old instructor name
          const oldInstructor = instructors.find(
            (i) => i.id === oldInstructorId
          );
          const oldInstructorName = oldInstructor
            ? `${oldInstructor.firstName} ${oldInstructor.lastName}`
            : "";

          // Add to history
          if (hoursWithOldInstructor.total > 0 || oldInstructorName) {
            instructorHistory.push({
              instructorId: oldInstructorId,
              instructorName: oldInstructorName,
              normalHours: hoursWithOldInstructor.normal,
              bonusHours: hoursWithOldInstructor.bonus,
              totalHours: hoursWithOldInstructor.total,
              dateChanged: new Date().toISOString(),
            });
          }
        }

        // Update existing candidate
        const candidateRef = doc(db, "candidates", editingCandidate.id);

        // Convert date fields from DD/MM/YYYY to ISO format
        const updateData = { ...formData };
        const dateFields = [
          "birthday",
          "startDate",
          "theoryTestDate",
          "practicalTestDate",
          "healthCertificateDate",
          "redCrossDate",
        ];
        dateFields.forEach((field) => {
          if (updateData[field]) {
            const converted = convertDateToISO(updateData[field]);
            updateData[field] = converted || updateData[field];
          }
        });

        await updateDoc(candidateRef, {
          ...updateData,
          instructorHistory: instructorHistory,
          updatedAt: serverTimestamp(),
        });

        // Create auth account if it doesn't exist yet
        if (!editingCandidate.authUid) {
          const email = generateEmail(formData.username, formData.email);
          if (email && formData.password) {
            // Combine phone prefix and number
            const fullPhoneNumber =
              formData.phonePrefix && formData.phoneNumber
                ? `${formData.phonePrefix}${formData.phoneNumber.replace(
                    /\s/g,
                    ""
                  )}`
                : formData.phoneNumber || null;

            // Get current admin user info before creating new user
            const currentAdmin = auth.currentUser;
            const adminEmail = currentAdmin?.email;

            const authResult = await createUserAccount(
              email,
              formData.password,
              "candidate",
              {
                firstName: formData.firstName,
                lastName: formData.lastName,
                phoneNumber: fullPhoneNumber,
              },
              editingCandidate.id,
              adminEmail,
              null // We don't have admin password, so session won't be restored automatically
            );

            if (authResult.success) {
              // Update candidate document with auth UID
              await updateDoc(candidateRef, {
                authUid: authResult.uid,
                email: email,
              });
            }
          }
        }

        const candidateName = `${formData.firstName} ${formData.lastName}`;
        await createNotification(
          "candidate_updated",
          "candidate",
          editingCandidate.id,
          candidateName
        );

        // Show specific message if instructor was changed
        if (
          oldInstructorId &&
          newInstructorId &&
          oldInstructorId !== newInstructorId
        ) {
          showToast(t("candidates.toast.instructorChanged"), "success");
        } else {
          showToast(t("candidates.toast.updated"), "success");
        }
      } else {
        // Create new candidate
        // Convert date fields from DD/MM/YYYY to ISO format
        const createData = { ...formData };
        const dateFields = [
          "birthday",
          "startDate",
          "theoryTestDate",
          "practicalTestDate",
          "healthCertificateDate",
          "redCrossDate",
        ];
        dateFields.forEach((field) => {
          if (createData[field]) {
            const converted = convertDateToISO(createData[field]);
            createData[field] = converted || createData[field];
          }
        });

        const docRef = await addDoc(collection(db, "candidates"), {
          ...createData,
          createdAt: serverTimestamp(),
        });

        // Create Firebase Auth account for the candidate
        const email = generateEmail(formData.username, formData.email);
        if (email && formData.password) {
          // Combine phone prefix and number
          const fullPhoneNumber =
            formData.phonePrefix && formData.phoneNumber
              ? `${formData.phonePrefix}${formData.phoneNumber.replace(
                  /\s/g,
                  ""
                )}`
              : formData.phoneNumber || null;

          // Get current admin user info before creating new user
          const currentAdmin = auth.currentUser;
          const adminEmail = currentAdmin?.email;

          const authResult = await createUserAccount(
            email,
            formData.password,
            "candidate",
            {
              firstName: formData.firstName,
              lastName: formData.lastName,
              phoneNumber: fullPhoneNumber,
            },
            docRef.id,
            adminEmail,
            null // We don't have admin password, so session won't be restored automatically
          );

          if (authResult.success) {
            // Update candidate document with auth UID
            await updateDoc(docRef, {
              authUid: authResult.uid,
              email: email,
            });
          } else {
            console.warn("Failed to create auth account:", authResult.error);
            // Still show success for candidate creation, but log the auth error
          }
        }

        const candidateName = `${formData.firstName} ${formData.lastName}`;
        await createNotification(
          "candidate_added",
          "candidate",
          docRef.id,
          candidateName
        );
        showToast(t("candidates.toast.created"), "success");
      }

      // Refresh candidates list
      fetchCandidates();
      handleCloseModal();
    } catch (error) {
      console.error("Error saving candidate:", error);
      showToast(t("candidates.toast.saveError"), "error");
    }
  };

  // Handle delete
  const handleDelete = async () => {
    if (!candidateToDelete) return;

    try {
      const candidateName = `${candidateToDelete.firstName} ${candidateToDelete.lastName}`;
      await deleteDoc(doc(db, "candidates", candidateToDelete.id));
      await createNotification(
        "candidate_deleted",
        "candidate",
        candidateToDelete.id,
        candidateName
      );
      showToast(t("candidates.toast.deleted"), "success");
      fetchCandidates();
      setIsDeleteModalOpen(false);
      setCandidateToDelete(null);
    } catch (error) {
      console.error("Error deleting candidate:", error);
      showToast(t("candidates.toast.deleteError"), "error");
    }
  };

  // Handle edit
  const handleEdit = (candidate) => {
    setEditingCandidate(candidate);

    // Handle backwards compatibility: convert old name-based data to ID-based
    let instructorId = candidate.assignedInstructorId || "";
    if (!instructorId && candidate.assignedInstructor) {
      // Old data format: has name but no ID
      instructorId = getInstructorIdByName(candidate.assignedInstructor);
    }

    // Handle phone number: extract prefix if exists, otherwise use default
    let phonePrefix = candidate.phonePrefix || "+383";
    let phoneNumber = candidate.phoneNumber || "";

    // If phoneNumber contains prefix (old format), extract it
    if (phoneNumber && phoneNumber.startsWith("+")) {
      const match = phoneNumber.match(/^(\+\d{1,4})\s*(.+)$/);
      if (match) {
        phonePrefix = match[1];
        phoneNumber = match[2];
      }
    }

    setFormData({
      candidateId: candidate.candidateId || "",
      firstName: candidate.firstName || "",
      lastName: candidate.lastName || "",
      phonePrefix: phonePrefix,
      phoneNumber: phoneNumber,
      email: candidate.email || "",
      username: candidate.username || "",
      personalNumber: candidate.personalNumber || "",
      password: candidate.password || "",
      birthday: formatDate(candidate.birthday) || "",
      gender: candidate.gender || "",
      address: candidate.address || "",
      parentName: candidate.parentName || "",
      category: candidate.category || "",
      status: candidate.status || "active",
      assignedInstructorId: instructorId,
      startDate: formatDate(candidate.startDate) || "",
      theoryTestDate: formatDate(candidate.theoryTestDate) || "",
      practicalTestDate: formatDate(candidate.practicalTestDate) || "",
      totalHours: candidate.totalHours || "",
      completedHours: candidate.completedHours || "",
      bonusHours: candidate.bonusHours || "",
      completedBonusHours: candidate.completedBonusHours || "",
      totalAmount: candidate.totalAmount || "",
      paidAmount: candidate.paidAmount || "",
      notes: candidate.notes || "",
      healthCertificateDate: formatDate(candidate.healthCertificateDate) || "",
      redCrossDate: formatDate(candidate.redCrossDate) || "",
      applicationAutoShkolla: candidate.applicationAutoShkolla || false,
      literature: candidate.literature || false,
      transmissionType: candidate.transmissionType || "",
      theoryTestStatus: candidate.theoryTestStatus || "notCompleted",
      practicalTestStatus: candidate.practicalTestStatus || "notCompleted",
      candidateRating: candidate.candidateRating || "",
    });
    setIsModalOpen(true);
  };

  // Handle view
  const handleView = (candidate) => {
    setViewingCandidate(candidate);
    setIsViewModalOpen(true);
  };

  // Handle close modal
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingCandidate(null);
    setFormData({
      candidateId: "",
      firstName: "",
      lastName: "",
      phonePrefix: "+383",
      phoneNumber: "",
      email: "",
      username: "",
      personalNumber: "",
      password: "",
      birthday: "",
      gender: "",
      address: "",
      parentName: "",
      category: "",
      status: "active",
      assignedInstructorId: "",
      startDate: "",
      theoryTestDate: "",
      practicalTestDate: "",
      totalHours: "",
      completedHours: "",
      bonusHours: "",
      completedBonusHours: "",
      totalAmount: "",
      paidAmount: "",
      notes: "",
      healthCertificateDate: "",
      redCrossDate: "",
      applicationAutoShkolla: false,
      literature: false,
      transmissionType: "",
      theoryTestStatus: "notCompleted",
      practicalTestStatus: "notCompleted",
      candidateRating: "",
    });
    setErrors({});
    setTouched({});
  };

  const mapCandidateToRow = (candidate) => ({
    "Candidate ID": candidate.candidateId,
    "First Name": candidate.firstName,
    "Last Name": candidate.lastName,
    "Phone Number": getFullPhoneNumber(candidate),
    Email: candidate.email,
    Username: candidate.username,
    "Personal Number": candidate.personalNumber,
    Birthday: formatDate(candidate.birthday),
    Gender: candidate.gender,
    Address: candidate.address,
    "Parent Name": candidate.parentName,
    Category: candidate.category,
    Status: candidate.status,
    "Assigned Instructor":
      getInstructorNameById(candidate.assignedInstructorId) ||
      candidate.assignedInstructor ||
      "N/A",
    "Start Date": formatDate(candidate.startDate),
    "Theory Test": formatDate(candidate.theoryTestDate),
    "Practical Test": formatDate(candidate.practicalTestDate),
    "Total Hours": candidate.totalHours,
    "Bonus Hours": candidate.bonusHours || 0,
    "Total Amount": candidate.totalAmount,
    "Paid Amount": candidate.paidAmount || 0,
    Notes: candidate.notes,
  });

  // Export to Excel (all candidates) - admin only
  const handleExportToExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(candidates.map(mapCandidateToRow));
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Candidates");
    XLSX.writeFile(
      workbook,
      `Candidates_${new Date().toISOString().split("T")[0]}.xlsx`
    );
  };

  // Export candidates by instructor (one sheet per instructor) - admin only
  const handleExportByInstructor = () => {
    const workbook = XLSX.utils.book_new();
    const unassigned = candidates.filter((c) => !c.assignedInstructorId || c.assignedInstructorId === "");
    const safeSheetName = (name) => (name || "").replace(/[:\\/?*\[\]]/g, "").substring(0, 31) || "Sheet";
    instructors.forEach((instructor) => {
      const instructorCandidates = candidates.filter(
        (c) => c.assignedInstructorId === instructor.id
      );
      const name = `${instructor.firstName || ""} ${instructor.lastName || ""}`.trim() || instructor.id;
      const ws = XLSX.utils.json_to_sheet(instructorCandidates.map(mapCandidateToRow));
      XLSX.utils.book_append_sheet(workbook, ws, safeSheetName(name) || safeSheetName(`Instructor_${instructor.id}`));
    });
    if (unassigned.length > 0) {
      const ws = XLSX.utils.json_to_sheet(unassigned.map(mapCandidateToRow));
      XLSX.utils.book_append_sheet(workbook, ws, safeSheetName(t("candidates.exportByInstructorUnassigned") || "Unassigned"));
    }
    XLSX.writeFile(
      workbook,
      `Candidates_By_Instructor_${new Date().toISOString().split("T")[0]}.xlsx`
    );
  };

  // Filter and search candidates
  const filteredAndSortedCandidates = React.useMemo(() => {
    // First, filter candidates by instructor if user is instructor
    let filtered = candidates;

    // For instructors, only show candidates assigned to them
    if (userRole === "instructor" && userEntityId) {
      filtered = candidates.filter((candidate) => {
        return candidate.assignedInstructorId === userEntityId;
      });
    }

    // Then apply other filters
    filtered = filtered.filter((candidate) => {
      const matchesSearch =
        !searchQuery ||
        candidate.candidateId
          ?.toLowerCase()
          .includes(searchQuery.toLowerCase()) ||
        candidate.firstName
          ?.toLowerCase()
          .includes(searchQuery.toLowerCase()) ||
        candidate.lastName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        candidate.phoneNumber?.includes(searchQuery) ||
        candidate.phonePrefix?.includes(searchQuery) ||
        getFullPhoneNumber(candidate)?.includes(searchQuery) ||
        candidate.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        candidate.username?.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesStatus = !filterStatus || candidate.status === filterStatus;
      const matchesCategory =
        !filterCategory || candidate.category === filterCategory;
      const matchesInstructor =
        !filterInstructor ||
        candidate.assignedInstructorId === filterInstructor ||
        candidate.assignedInstructor === filterInstructor; // backwards compatibility

      // Date range filter (based on startDate)
      let matchesDateRange = true;
      if (filterDateFrom || filterDateTo) {
        const candidateStartDate = candidate.startDate
          ? new Date(candidate.startDate)
          : null;

        if (!candidateStartDate || isNaN(candidateStartDate.getTime())) {
          matchesDateRange = false;
        } else {
          // Set time to start of day for comparison
          const candidateDate = new Date(
            candidateStartDate.getFullYear(),
            candidateStartDate.getMonth(),
            candidateStartDate.getDate()
          );

          if (filterDateFrom) {
            const fromDate = parseDateString(filterDateFrom);
            if (fromDate) {
              fromDate.setHours(0, 0, 0, 0);
              if (candidateDate < fromDate) {
                matchesDateRange = false;
              }
            }
          }

          if (filterDateTo) {
            const toDate = parseDateString(filterDateTo);
            if (toDate) {
              toDate.setHours(23, 59, 59, 999);
              if (candidateDate > toDate) {
                matchesDateRange = false;
              }
            }
          }
        }
      }

      return (
        matchesSearch &&
        matchesStatus &&
        matchesCategory &&
        matchesInstructor &&
        matchesDateRange
      );
    });

    // Then, sort candidates
    filtered.sort((a, b) => {
      // Order: active first, then onProcess, then others (completed, paused, failed)
      const statusOrder = {
        active: 1,
        onProcess: 2,
        completed: 3,
        paused: 4,
        failed: 5,
      };
      
      const aOrder = statusOrder[a.status] || 99;
      const bOrder = statusOrder[b.status] || 99;

      if (aOrder !== bOrder) return aOrder - bOrder;

      // If both completed or both not completed, apply the selected sort
      switch (sortBy) {
        case "date-newest":
          return new Date(b.startDate || 0) - new Date(a.startDate || 0);
        case "date-oldest":
          return new Date(a.startDate || 0) - new Date(b.startDate || 0);
        case "name-az":
          return (a.firstName || "").localeCompare(b.firstName || "");
        case "name-za":
          return (b.firstName || "").localeCompare(a.firstName || "");
        default:
          return new Date(b.startDate || 0) - new Date(a.startDate || 0);
      }
    });

    return filtered;
  }, [
    candidates,
    userRole,
    userEntityId,
    searchQuery,
    filterStatus,
    filterCategory,
    filterInstructor,
    filterDateFrom,
    filterDateTo,
    sortBy,
  ]);

  // Pagination calculations
  const totalPages = Math.ceil(
    filteredAndSortedCandidates.length / itemsPerPage
  );
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedCandidates = filteredAndSortedCandidates.slice(
    startIndex,
    endIndex
  );

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [
    searchQuery,
    filterStatus,
    filterCategory,
    filterInstructor,
    filterDateFrom,
    filterDateTo,
    sortBy,
  ]);

  // Get unique values for filters
  const uniqueCategories = [
    ...new Set(candidates.map((c) => c.category)),
  ].filter(Boolean);

  // Get all instructors (sorted by name) for the filter dropdown
  const sortedInstructors = [...instructors].sort((a, b) =>
    `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`)
  );

  // Get available years from candidates (based on startDate)
  const availableYears = React.useMemo(() => {
    const years = new Set();
    candidates.forEach((candidate) => {
      const startDate = candidate.startDate
        ? new Date(candidate.startDate)
        : null;
      if (startDate && !isNaN(startDate.getTime())) {
        years.add(startDate.getFullYear());
      }
    });
    return Array.from(years).sort((a, b) => b - a); // Sort descending (newest first)
  }, [candidates]);

  // Get available months for selected year
  const availableMonths = React.useMemo(() => {
    if (selectedYear === "all") return [];

    const months = new Set();
    candidates.forEach((candidate) => {
      const startDate = candidate.startDate
        ? new Date(candidate.startDate)
        : null;
      if (
        startDate &&
        !isNaN(startDate.getTime()) &&
        startDate.getFullYear() === parseInt(selectedYear)
      ) {
        months.add(startDate.getMonth() + 1); // Month is 0-indexed, so add 1
      }
    });
    return Array.from(months).sort((a, b) => a - b); // Sort ascending (1-12)
  }, [candidates, selectedYear]);

  // Reset month when year changes to "all"
  useEffect(() => {
    if (selectedYear === "all") {
      setSelectedMonth("all");
    }
  }, [selectedYear]);

  // Calculate stats
  const stats = React.useMemo(() => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const thisMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const thisYear = new Date(now.getFullYear(), 0, 1);

    // Filter candidates for table display (by selected year and month)
    const filteredCandidates = candidates.filter((candidate) => {
      const startDate = candidate.startDate
        ? new Date(candidate.startDate)
        : null;
      if (!startDate || isNaN(startDate.getTime())) return false;

      // Filter by year
      if (selectedYear !== "all") {
        if (startDate.getFullYear() !== parseInt(selectedYear)) return false;
      }

      // Filter by month (only if year is selected)
      if (selectedYear !== "all" && selectedMonth !== "all") {
        if (startDate.getMonth() + 1 !== parseInt(selectedMonth)) return false;
      }

      return true;
    });

    // Determine the month range for stats (based on selected month, or current month)
    let selectedMonthStart, selectedMonthEnd;
    if (selectedYear !== "all" && selectedMonth !== "all") {
      // Use selected month
      selectedMonthStart = new Date(
        parseInt(selectedYear),
        parseInt(selectedMonth) - 1,
        1
      );
      selectedMonthEnd = new Date(
        parseInt(selectedYear),
        parseInt(selectedMonth) - 1,
        new Date(parseInt(selectedYear), parseInt(selectedMonth), 0).getDate(),
        23,
        59,
        59
      );
    } else {
      // Use current month
      selectedMonthStart = thisMonth;
      selectedMonthEnd = now;
    }

    // Determine the year range for stats (always the whole year, not just selected month)
    let selectedYearStart, selectedYearEnd;
    if (selectedYear === "all") {
      selectedYearStart = thisYear;
      selectedYearEnd = new Date(now.getFullYear(), 11, 31, 23, 59, 59);
    } else {
      // Always use the whole year
      selectedYearStart = new Date(parseInt(selectedYear), 0, 1);
      selectedYearEnd = new Date(parseInt(selectedYear), 11, 31, 23, 59, 59);
    }

    // Filter candidates for year stats (by year only, not month)
    const yearFilteredCandidates = candidates.filter((candidate) => {
      const startDate = candidate.startDate
        ? new Date(candidate.startDate)
        : null;
      if (!startDate || isNaN(startDate.getTime())) return false;

      // Filter by year only
      if (selectedYear !== "all") {
        if (startDate.getFullYear() !== parseInt(selectedYear)) return false;
      }

      return true;
    });

    // Filter candidates for month stats (by selected month, or all for current month)
    const monthFilteredCandidates =
      selectedYear !== "all" && selectedMonth !== "all"
        ? candidates.filter((candidate) => {
            const startDate = candidate.startDate
              ? new Date(candidate.startDate)
              : null;
            if (!startDate || isNaN(startDate.getTime())) return false;
            return (
              startDate.getFullYear() === parseInt(selectedYear) &&
              startDate.getMonth() + 1 === parseInt(selectedMonth)
            );
          })
        : candidates; // Use all candidates for current month calculation

    let todayTotal = 0;
    let todayMale = 0;
    let todayFemale = 0;
    let monthTotal = 0;
    let monthMale = 0;
    let monthFemale = 0;
    let yearTotal = 0;
    let yearMale = 0;
    let yearFemale = 0;
    let allTimeTotal = candidates.length;
    let allTimeMale = 0;
    let allTimeFemale = 0;

    // Calculate "today" stats from ALL candidates (not filtered)
    candidates.forEach((candidate) => {
      const startDate = candidate.startDate
        ? new Date(candidate.startDate)
        : null;

      if (startDate && !isNaN(startDate.getTime())) {
        // Today
        if (startDate >= today) {
          todayTotal++;
          if (candidate.gender === "male") todayMale++;
          else if (candidate.gender === "female") todayFemale++;
        }
      }
    });

    // Calculate "month" stats from month-filtered candidates
    monthFilteredCandidates.forEach((candidate) => {
      const startDate = candidate.startDate
        ? new Date(candidate.startDate)
        : null;

      if (startDate && !isNaN(startDate.getTime())) {
        // Selected Month (or current month if no month selected)
        if (startDate >= selectedMonthStart && startDate <= selectedMonthEnd) {
          monthTotal++;
          if (candidate.gender === "male") monthMale++;
          else if (candidate.gender === "female") monthFemale++;
        }
      }
    });

    // Calculate "year" stats from year-filtered candidates (not month-filtered)
    yearFilteredCandidates.forEach((candidate) => {
      const startDate = candidate.startDate
        ? new Date(candidate.startDate)
        : null;

      if (startDate && !isNaN(startDate.getTime())) {
        // Selected Year / This Year (always whole year)
        if (startDate >= selectedYearStart && startDate <= selectedYearEnd) {
          yearTotal++;
          if (candidate.gender === "male") yearMale++;
          else if (candidate.gender === "female") yearFemale++;
        }
      }
    });

    // Calculate "all time" stats from ALL candidates (not filtered)
    candidates.forEach((candidate) => {
      if (candidate.gender === "male") {
        allTimeMale++;
      } else if (candidate.gender === "female") {
        allTimeFemale++;
      }
    });

    return {
      today: {
        total: todayTotal,
        male: todayMale,
        female: todayFemale,
      },
      month: {
        total: monthTotal,
        male: monthMale,
        female: monthFemale,
      },
      year: {
        total: yearTotal,
        male: yearMale,
        female: yearFemale,
      },
      allTime: {
        total: allTimeTotal,
        male: allTimeMale,
        female: allTimeFemale,
      },
    };
  }, [candidates, selectedYear, selectedMonth]);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              {t("candidates.title")}
            </h2>
            <p className="text-gray-600">{t("candidates.subtitle")}</p>
          </div>
          <div className="flex flex-wrap gap-3">
            {isAdmin && (
              <>
                <button
                  onClick={handleExportToExcel}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium flex items-center space-x-2"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                  <span>{t("candidates.exportToExcel")}</span>
                </button>
                <button
                  onClick={handleExportByInstructor}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium flex items-center space-x-2"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                  <span>{t("candidates.exportByInstructor")}</span>
                </button>
              </>
            )}
            {isAdmin && (
              <button
                onClick={() => setIsModalOpen(true)}
                className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors font-medium flex items-center space-x-2"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 4v16m8-8H4"
                  />
                </svg>
                <span>{t("candidates.addCandidate")}</span>
              </button>
            )}
          </div>
        </div>

        {/* Year and Month Selectors */}
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center gap-3 flex-wrap">
            <label className="text-sm font-medium text-gray-700">
              {t("candidates.stats.filterByYear")}:
            </label>
            <select
              value={selectedYear}
              onChange={(e) => {
                setSelectedYear(e.target.value);
                setSelectedMonth("all"); // Reset month when year changes
              }}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10 focus:border-black text-sm bg-white"
            >
              <option value="all">{t("candidates.stats.allYears")}</option>
              {availableYears.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>

            {selectedYear !== "all" && (
              <>
                <label className="text-sm font-medium text-gray-700 ml-2">
                  {t("candidates.stats.filterByMonth")}:
                </label>
                <select
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10 focus:border-black text-sm bg-white"
                >
                  <option value="all">{t("candidates.stats.allMonths")}</option>
                  {availableMonths.map((month) => (
                    <option key={month} value={month}>
                      {t(`candidates.stats.months.${month}`)}
                    </option>
                  ))}
                </select>
              </>
            )}
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Today */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-sm font-medium text-gray-500 mb-4">
              {t("candidates.stats.today")}
            </h3>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">
                  {t("candidates.stats.male")}
                </span>
                <div className="flex items-center space-x-2">
                  <span className="font-semibold text-green-600">
                    {stats.today.male}
                  </span>
                  {stats.today.total > 0 && (
                    <span className="text-xs text-gray-500">
                      (
                      {((stats.today.male / stats.today.total) * 100).toFixed(
                        1
                      )}
                      %)
                    </span>
                  )}
                </div>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">
                  {t("candidates.stats.female")}
                </span>
                <div className="flex items-center space-x-2">
                  <span className="font-semibold text-red-600">
                    {stats.today.female}
                  </span>
                  {stats.today.total > 0 && (
                    <span className="text-xs text-gray-500">
                      (
                      {((stats.today.female / stats.today.total) * 100).toFixed(
                        1
                      )}
                      %)
                    </span>
                  )}
                </div>
              </div>
              <div className="flex justify-between pt-2 border-t border-gray-200">
                <span className="text-sm font-medium text-gray-900">
                  {t("candidates.stats.total")}
                </span>
                <span className="font-bold text-gray-900">
                  {stats.today.total}
                </span>
              </div>
            </div>
          </div>

          {/* This Month */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-sm font-medium text-gray-500 mb-4">
              {selectedYear !== "all" && selectedMonth !== "all"
                ? `${t(
                    `candidates.stats.months.${selectedMonth}`
                  )} ${selectedYear}`
                : t("candidates.stats.thisMonth")}
            </h3>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">
                  {t("candidates.stats.male")}
                </span>
                <div className="flex items-center space-x-2">
                  <span className="font-semibold text-green-600">
                    {stats.month.male}
                  </span>
                  {stats.month.total > 0 && (
                    <span className="text-xs text-gray-500">
                      (
                      {((stats.month.male / stats.month.total) * 100).toFixed(
                        1
                      )}
                      %)
                    </span>
                  )}
                </div>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">
                  {t("candidates.stats.female")}
                </span>
                <div className="flex items-center space-x-2">
                  <span className="font-semibold text-red-600">
                    {stats.month.female}
                  </span>
                  {stats.month.total > 0 && (
                    <span className="text-xs text-gray-500">
                      (
                      {((stats.month.female / stats.month.total) * 100).toFixed(
                        1
                      )}
                      %)
                    </span>
                  )}
                </div>
              </div>
              <div className="flex justify-between pt-2 border-t border-gray-200">
                <span className="text-sm font-medium text-gray-900">
                  {t("candidates.stats.total")}
                </span>
                <span className="font-bold text-gray-900">
                  {stats.month.total}
                </span>
              </div>
            </div>
          </div>

          {/* This Year / Selected Year */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-sm font-medium text-gray-500 mb-4">
              {selectedYear === "all"
                ? t("candidates.stats.thisYear")
                : selectedYear}
            </h3>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">
                  {t("candidates.stats.male")}
                </span>
                <div className="flex items-center space-x-2">
                  <span className="font-semibold text-green-600">
                    {stats.year.male}
                  </span>
                  {stats.year.total > 0 && (
                    <span className="text-xs text-gray-500">
                      ({((stats.year.male / stats.year.total) * 100).toFixed(1)}
                      %)
                    </span>
                  )}
                </div>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">
                  {t("candidates.stats.female")}
                </span>
                <div className="flex items-center space-x-2">
                  <span className="font-semibold text-red-600">
                    {stats.year.female}
                  </span>
                  {stats.year.total > 0 && (
                    <span className="text-xs text-gray-500">
                      (
                      {((stats.year.female / stats.year.total) * 100).toFixed(
                        1
                      )}
                      %)
                    </span>
                  )}
                </div>
              </div>
              <div className="flex justify-between pt-2 border-t border-gray-200">
                <span className="text-sm font-medium text-gray-900">
                  {t("candidates.stats.total")}
                </span>
                <span className="font-bold text-gray-900">
                  {stats.year.total}
                </span>
              </div>
            </div>
          </div>

          {/* All Time */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-sm font-medium text-gray-500 mb-4">
              {t("candidates.stats.allTime")}
            </h3>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">
                  {t("candidates.stats.male")}
                </span>
                <div className="flex items-center space-x-2">
                  <span className="font-semibold text-green-600">
                    {stats.allTime.male}
                  </span>
                  {stats.allTime.total > 0 && (
                    <span className="text-xs text-gray-500">
                      (
                      {(
                        (stats.allTime.male / stats.allTime.total) *
                        100
                      ).toFixed(1)}
                      %)
                    </span>
                  )}
                </div>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">
                  {t("candidates.stats.female")}
                </span>
                <div className="flex items-center space-x-2">
                  <span className="font-semibold text-red-600">
                    {stats.allTime.female}
                  </span>
                  {stats.allTime.total > 0 && (
                    <span className="text-xs text-gray-500">
                      (
                      {(
                        (stats.allTime.female / stats.allTime.total) *
                        100
                      ).toFixed(1)}
                      %)
                    </span>
                  )}
                </div>
              </div>
              <div className="flex justify-between pt-2 border-t border-gray-200">
                <span className="text-sm font-medium text-gray-900">
                  {t("candidates.stats.total")}
                </span>
                <span className="font-bold text-gray-900">
                  {stats.allTime.total}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-3">
          {/* Search Bar Row */}
          <div className="relative">
            <input
              type="text"
              placeholder={t("candidates.search")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-4 py-3 pl-10 border-2 border-gray-400 rounded-lg bg-gray-50 font-semibold text-gray-900 placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-black/25 focus:border-black text-base"
            />
            <svg
              className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>

          {/* Filters Row */}
          <div className="flex flex-wrap items-center gap-2">
            {/* Sort By */}
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10 focus:border-black text-sm bg-white"
            >
              <option value="date-newest">
                {t("candidates.sort.newestFirst")}
              </option>
              <option value="date-oldest">
                {t("candidates.sort.oldestFirst")}
              </option>
              <option value="name-az">{t("candidates.sort.nameAZ")}</option>
              <option value="name-za">{t("candidates.sort.nameZA")}</option>
            </select>

            {/* Status Filter */}
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10 focus:border-black text-sm bg-white"
            >
              <option value="">{t("candidates.filters.allStatuses")}</option>
              <option value="active">{t("candidates.filters.active")}</option>
              <option value="completed">
                {t("candidates.filters.completed")}
              </option>
              <option value="onProcess">{t("candidates.filters.onProcess")}</option>
              <option value="paused">{t("candidates.filters.paused")}</option>
              <option value="failed">{t("candidates.filters.failed")}</option>
            </select>

            {/* Category Filter */}
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10 focus:border-black text-sm bg-white"
            >
              <option value="">{t("candidates.filters.allCategories")}</option>
              {uniqueCategories.map((category) => (
                <option key={category} value={category}>
                  {t("candidates.filters.category")} {category}
                </option>
              ))}
            </select>

            {/* Instructor Filter */}
            <select
              value={filterInstructor}
              onChange={(e) => setFilterInstructor(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10 focus:border-black text-sm bg-white"
            >
              <option value="">{t("candidates.filters.allInstructors")}</option>
              {sortedInstructors.map((instructor) => (
                <option key={instructor.id} value={instructor.id}>
                  {instructor.firstName} {instructor.lastName}
                </option>
              ))}
            </select>

            {/* Date From Filter */}
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700 whitespace-nowrap">
                {t("candidates.filters.dateFrom")}:
              </label>
              <input
                type="date"
                value={
                  filterDateFrom
                    ? formatDateForInput(
                        parseDateString(filterDateFrom) || filterDateFrom
                      )
                    : ""
                }
                onChange={(e) => {
                  const value = e.target.value;
                  if (value) {
                    const date = new Date(value);
                    setFilterDateFrom(formatDate(date));
                  } else {
                    setFilterDateFrom("");
                  }
                }}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10 focus:border-black text-sm bg-white"
              />
            </div>

            {/* Date To Filter */}
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700 whitespace-nowrap">
                {t("candidates.filters.dateTo")}:
              </label>
              <input
                type="date"
                value={
                  filterDateTo
                    ? formatDateForInput(
                        parseDateString(filterDateTo) || filterDateTo
                      )
                    : ""
                }
                onChange={(e) => {
                  const value = e.target.value;
                  if (value) {
                    const date = new Date(value);
                    // Validate that "to date" is not before "from date"
                    if (filterDateFrom) {
                      const fromDate = parseDateString(filterDateFrom);
                      if (fromDate && date < fromDate) {
                        return; // Don't update if invalid
                      }
                    }
                    setFilterDateTo(formatDate(date));
                  } else {
                    setFilterDateTo("");
                  }
                }}
                min={
                  filterDateFrom
                    ? formatDateForInput(
                        parseDateString(filterDateFrom) || filterDateFrom
                      )
                    : undefined
                }
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10 focus:border-black text-sm bg-white"
              />
            </div>

            {/* Clear Filters Button */}
            {(searchQuery ||
              filterStatus ||
              filterCategory ||
              filterInstructor ||
              filterDateFrom ||
              filterDateTo ||
              sortBy !== "date-newest") && (
              <button
                onClick={() => {
                  setSearchQuery("");
                  setFilterStatus("");
                  setFilterCategory("");
                  setFilterInstructor("");
                  setFilterDateFrom("");
                  setFilterDateTo("");
                  setSortBy("date-newest");
                }}
                className="ml-auto px-3 py-2 text-sm text-gray-600 hover:text-black hover:bg-gray-100 rounded-lg transition-colors flex items-center space-x-1"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
                <span>{t("candidates.clearAll")}</span>
              </button>
            )}
          </div>
        </div>

        {/* Results Count */}
        {!loading && candidates.length > 0 && (
          <div className="flex items-center justify-between text-sm text-gray-600 px-2">
            <span>
              {t("candidates.showing")}{" "}
              <span className="font-semibold text-gray-900">
                {filteredAndSortedCandidates.length > 0
                  ? `${startIndex + 1}-${Math.min(
                      endIndex,
                      filteredAndSortedCandidates.length
                    )}`
                  : 0}
              </span>{" "}
              {t("candidates.of")}{" "}
              <span className="font-semibold text-gray-900">
                {filteredAndSortedCandidates.length}
              </span>{" "}
              {t("candidates.candidatesText")}
              {filteredAndSortedCandidates.length !== candidates.length && (
                <span className="text-gray-500">
                  {" "}
                  ({t("candidates.of")} {candidates.length}{" "}
                  {t("candidates.total")})
                </span>
              )}
            </span>
          </div>
        )}

        {/* Candidates Table */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          {filteredAndSortedCandidates.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-gray-500">
              <svg
                className="w-16 h-16 mb-4 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                />
              </svg>
              <p>{t("candidates.noCandidates")}</p>
              {isAdmin && (
                <button
                  onClick={() => setIsModalOpen(true)}
                  className="mt-4 text-black hover:underline"
                >
                  {t("candidates.addFirstCandidate")}
                </button>
              )}
            </div>
          ) : (
            <>
              {/* Card view - small screens only */}
              <div className="md:hidden divide-y divide-gray-200">
                {paginatedCandidates.map((candidate) => (
                  <div
                    key={candidate.id}
                    className={`p-4 transition-colors ${
                      candidate.status === "completed"
                        ? "bg-green-50"
                        : candidate.status === "onProcess"
                        ? "bg-purple-50"
                        : "bg-white hover:bg-gray-50"
                    }`}
                  >
                    <div className="min-w-0">
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-0.5">
                          {t("candidates.table.candidateId")}
                        </p>
                        <p className="text-base font-semibold text-gray-900 mb-1">
                          {candidate.candidateId}
                        </p>
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-0.5">
                          {t("candidates.table.name")}
                        </p>
                        <div className="flex items-center gap-2 mb-3">
                          <div
                            className={`flex-shrink-0 w-3 h-3 rounded-full ${getStatusColor(
                              candidate.status
                            )}`}
                          />
                          <span className="text-lg font-bold text-gray-900 truncate">
                            {candidate.firstName} {candidate.lastName}
                          </span>
                        </div>
                        {candidate.username && (
                          <p className="text-sm text-gray-500 truncate mb-3">@{candidate.username}</p>
                        )}
                        {/* Hours - separate lines, bigger font on mobile */}
                        <div className="mb-3 space-y-1">
                          <p className="text-base text-gray-600">
                            <span className="font-medium text-gray-500">{t("lessons.stats.remainingHours")}: </span>
                            <span className="text-lg font-bold text-gray-900">
                              {Math.max(0, (Number(candidate.totalHours) || 0) - (Number(candidate.completedHours) || 0))}
                            </span>
                          </p>
                          <p className="text-base text-gray-600">
                            <span className="font-medium text-gray-500">{t("lessons.stats.totalHours")}: </span>
                            <span className="text-lg font-bold text-gray-900">{candidate.totalHours || 0}</span>
                          </p>
                          {candidate.bonusHours ? (
                            <p className="text-base text-gray-600">
                              <span className="font-medium text-gray-500">{t("candidates.table.bonus")}: </span>
                              <span className="text-lg font-bold text-gray-900">
                                {(Number(candidate.bonusHours) || 0) - (Number(candidate.completedBonusHours) || 0)} / {candidate.bonusHours || 0}
                              </span>
                            </p>
                          ) : null}
                        </div>
                        <div className="flex flex-wrap gap-x-3 gap-y-1 text-sm text-gray-600">
                          <span>{getFullPhoneNumber(candidate)}</span>
                          <span className="px-2 py-0.5 rounded-full bg-blue-100 text-blue-800 text-xs font-medium">
                            {candidate.category}
                          </span>
                          {candidate.transmissionType && (
                            <span className="text-xs text-gray-500">
                              {t(
                                `candidates.form.transmissionTypeOptions.${candidate.transmissionType}`
                              )}
                            </span>
                          )}
                        </div>
                        <p className="mt-1 text-xs text-gray-500">
                          {t("candidates.table.instructor")}:{" "}
                          {getInstructorNameById(candidate.assignedInstructorId) ||
                            candidate.assignedInstructor || (
                              <span className="italic text-gray-400">
                                {t("candidates.table.notAssigned")}
                              </span>
                            )}
                        </p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {t("candidates.table.startDate")}: {formatDate(candidate.startDate)}
                        </p>
                    </div>
                    {/* Action icons - own row */}
                    <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-gray-200">
                        <button
                          onClick={() => openWhatsApp(candidate)}
                          className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                          title={t("candidates.actions.whatsapp")}
                        >
                          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => openViber(candidate)}
                          className="p-2 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                          title={t("candidates.actions.viber")}
                        >
                          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M13 4C12.4477 4 12 4.44772 12 5C12 5.55228 12.4477 6 13 6C14.2728 6 15.2557 6.41989 15.9179 7.08211C16.5801 7.74433 17 8.72725 17 10C17 10.5523 17.4477 11 18 11C18.5523 11 19 10.5523 19 10C19 8.27275 18.4199 6.75567 17.3321 5.66789C16.2443 4.58011 14.7272 4 13 4Z" />
                            <path d="M5.014 8.00613C5.12827 7.1024 6.30277 5.87414 7.23488 6.01043L7.23339 6.00894C8.01251 6.15699 8.65217 7.32965 9.07373 8.10246C9.14298 8.22942 9.20635 8.34559 9.26349 8.44465C9.55041 8.95402 9.3641 9.4701 9.09655 9.68787C9.06561 9.7128 9.03317 9.73855 8.9998 9.76504C8.64376 10.0477 8.18114 10.4149 8.28943 10.7834C8.5 11.5 11 14 12.2296 14.7107C12.6061 14.9283 12.8988 14.5057 13.1495 14.1438C13.2087 14.0583 13.2656 13.9762 13.3207 13.9067C13.5301 13.6271 14.0466 13.46 14.5548 13.736C15.3138 14.178 16.0288 14.6917 16.69 15.27C17.0202 15.546 17.0977 15.9539 16.8689 16.385C16.4659 17.1443 15.3003 18.1456 14.4542 17.9421C12.9764 17.5868 7 15.27 5.08033 8.55801C4.97981 8.26236 4.99645 8.13792 5.01088 8.02991L5.014 8.00613Z" />
                            <path fillRule="evenodd" clipRule="evenodd" d="M7.51742 23.8312C7.54587 23.8469 7.57508 23.8612 7.60492 23.874C8.14762 24.1074 8.81755 23.5863 10.1574 22.5442L11.5 21.5C14.1884 21.589 16.514 21.2362 18.312 20.6071C20.3227 19.9035 21.9036 18.3226 22.6072 16.3119C23.5768 13.541 23.5768 8.45883 22.6072 5.68794C21.9036 3.67722 20.3227 2.0963 18.312 1.39271C15.1103 0.272407 8.82999 0.293306 5.68806 1.39271C3.67733 2.0963 2.09642 3.67722 1.39283 5.68794C0.423255 8.45883 0.423255 13.541 1.39283 16.3119C2.09642 18.3226 3.67733 19.9035 5.68806 20.6071C6.08252 20.7451 6.52371 20.8965 7 21C7 22.6974 7 23.5461 7.51742 23.8312ZM9 20.9107V19.7909C9 19.5557 8.836 19.3524 8.60597 19.3032C7.84407 19.1403 7.08676 18.9776 6.34862 18.7193C4.91238 18.2168 3.78316 17.0875 3.2806 15.6513C2.89871 14.5599 2.66565 12.8453 2.66565 10.9999C2.66565 9.15453 2.89871 7.43987 3.2806 6.3485C3.78316 4.91227 4.91238 3.78304 6.34862 3.28048C7.61625 2.83692 9.71713 2.56282 11.9798 2.56032C14.2422 2.55782 16.3561 2.82723 17.6514 3.28048C19.0876 3.78304 20.2169 4.91227 20.7194 6.3485C21.1013 7.43987 21.3344 9.15453 21.3344 10.9999C21.3344 12.8453 21.1013 14.5599 20.7194 15.6513C20.2169 17.0875 19.0876 18.2168 17.6514 18.7193C15.5197 19.4652 13.259 19.549 11.0239 19.4828C10.9071 19.4794 10.7926 19.5165 10.7004 19.5882L9 20.9107Z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleView(candidate)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title={t("candidates.actions.view")}
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        </button>
                        {isAdmin && (
                          <>
                            <button
                              onClick={() => handleEdit(candidate)}
                              className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                              title={t("candidates.actions.edit")}
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </button>
                            <button
                              onClick={() => {
                                setCandidateToDelete(candidate);
                                setIsDeleteModalOpen(true);
                              }}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title={t("candidates.actions.delete")}
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </>
                        )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Table view - medium screens and up */}
              <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="w-4 px-2 py-3"></th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t("candidates.table.candidateId")}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t("candidates.table.name")}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t("candidates.table.phoneNumber")}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t("candidates.table.category")}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t("candidates.table.transmissionType")}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t("candidates.table.instructor")}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t("candidates.table.hours")}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t("candidates.table.startDate")}
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t("candidates.table.actions")}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {paginatedCandidates.map((candidate) => (
                    <tr
                      key={candidate.id}
                      className={`transition-colors ${
                        candidate.status === "completed"
                          ? "bg-green-50 hover:bg-green-100"
                          : candidate.status === "onProcess"
                          ? "bg-purple-50 hover:bg-purple-100"
                          : "hover:bg-gray-50"
                      }`}
                    >
                      <td className="px-2 py-4">
                        <div
                          className={`w-3 h-3 rounded-full ${getStatusColor(
                            candidate.status
                          )}`}
                        ></div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="font-semibold text-gray-900">
                          {candidate.candidateId}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="font-medium text-gray-900">
                          {candidate.firstName} {candidate.lastName}
                        </div>
                        <div className="text-sm text-gray-500">
                          @{candidate.username}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-2">
                          <span className="text-sm text-gray-900">
                            {getFullPhoneNumber(candidate)}
                          </span>
                          <button
                            onClick={() => openWhatsApp(candidate)}
                            className="text-green-600 hover:text-green-700 transition-colors"
                            title={t("candidates.actions.whatsapp")}
                          >
                            <svg
                              className="w-5 h-5"
                              fill="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                            </svg>
                          </button>
                          <button
                            onClick={() => openViber(candidate)}
                            className="text-purple-600 hover:text-purple-700 transition-colors"
                            title={t("candidates.actions.viber")}
                          >
                            <svg
                              className="w-5 h-5"
                              viewBox="0 0 24 24"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M13 4C12.4477 4 12 4.44772 12 5C12 5.55228 12.4477 6 13 6C14.2728 6 15.2557 6.41989 15.9179 7.08211C16.5801 7.74433 17 8.72725 17 10C17 10.5523 17.4477 11 18 11C18.5523 11 19 10.5523 19 10C19 8.27275 18.4199 6.75567 17.3321 5.66789C16.2443 4.58011 14.7272 4 13 4Z"
                                fill="currentColor"
                              />
                              <path
                                d="M5.014 8.00613C5.12827 7.1024 6.30277 5.87414 7.23488 6.01043L7.23339 6.00894C8.01251 6.15699 8.65217 7.32965 9.07373 8.10246C9.14298 8.22942 9.20635 8.34559 9.26349 8.44465C9.55041 8.95402 9.3641 9.4701 9.09655 9.68787C9.06561 9.7128 9.03317 9.73855 8.9998 9.76504C8.64376 10.0477 8.18114 10.4149 8.28943 10.7834C8.5 11.5 11 14 12.2296 14.7107C12.6061 14.9283 12.8988 14.5057 13.1495 14.1438C13.2087 14.0583 13.2656 13.9762 13.3207 13.9067C13.5301 13.6271 14.0466 13.46 14.5548 13.736C15.3138 14.178 16.0288 14.6917 16.69 15.27C17.0202 15.546 17.0977 15.9539 16.8689 16.385C16.4659 17.1443 15.3003 18.1456 14.4542 17.9421C12.9764 17.5868 7 15.27 5.08033 8.55801C4.97981 8.26236 4.99645 8.13792 5.01088 8.02991L5.014 8.00613Z"
                                fill="currentColor"
                              />
                              <path
                                d="M13 7C12.4477 7 12 7.44772 12 8C12 8.55228 12.4477 9 13 9C13.1748 9 13.4332 9.09745 13.6679 9.33211C13.9025 9.56676 14 9.82523 14 10C14 10.5523 14.4477 11 15 11C15.5523 11 16 10.5523 16 10C16 9.17477 15.5975 8.43324 15.0821 7.91789C14.5668 7.40255 13.8252 7 13 7Z"
                                fill="currentColor"
                              />
                              <path
                                fillRule="evenodd"
                                clipRule="evenodd"
                                d="M7.51742 23.8312C7.54587 23.8469 7.57508 23.8612 7.60492 23.874C8.14762 24.1074 8.81755 23.5863 10.1574 22.5442L11.5 21.5C14.1884 21.589 16.514 21.2362 18.312 20.6071C20.3227 19.9035 21.9036 18.3226 22.6072 16.3119C23.5768 13.541 23.5768 8.45883 22.6072 5.68794C21.9036 3.67722 20.3227 2.0963 18.312 1.39271C15.1103 0.272407 8.82999 0.293306 5.68806 1.39271C3.67733 2.0963 2.09642 3.67722 1.39283 5.68794C0.423255 8.45883 0.423255 13.541 1.39283 16.3119C2.09642 18.3226 3.67733 19.9035 5.68806 20.6071C6.08252 20.7451 6.52371 20.8965 7 21C7 22.6974 7 23.5461 7.51742 23.8312ZM9 20.9107V19.7909C9 19.5557 8.836 19.3524 8.60597 19.3032C7.84407 19.1403 7.08676 18.9776 6.34862 18.7193C4.91238 18.2168 3.78316 17.0875 3.2806 15.6513C2.89871 14.5599 2.66565 12.8453 2.66565 10.9999C2.66565 9.15453 2.89871 7.43987 3.2806 6.3485C3.78316 4.91227 4.91238 3.78304 6.34862 3.28048C7.61625 2.83692 9.71713 2.56282 11.9798 2.56032C14.2422 2.55782 16.3561 2.82723 17.6514 3.28048C19.0876 3.78304 20.2169 4.91227 20.7194 6.3485C21.1013 7.43987 21.3344 9.15453 21.3344 10.9999C21.3344 12.8453 21.1013 14.5599 20.7194 15.6513C20.2169 17.0875 19.0876 18.2168 17.6514 18.7193C15.5197 19.4652 13.259 19.549 11.0239 19.4828C10.9071 19.4794 10.7926 19.5165 10.7004 19.5882L9 20.9107Z"
                                fill="currentColor"
                              />
                            </svg>
                          </button>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-3 py-1 inline-flex text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                          {candidate.category}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {candidate.transmissionType ? (
                          t(
                            `candidates.form.transmissionTypeOptions.${candidate.transmissionType}`
                          )
                        ) : (
                          <span className="text-gray-400 italic">
                            {t("candidates.table.notAssigned")}
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {getInstructorNameById(
                          candidate.assignedInstructorId
                        ) ||
                          candidate.assignedInstructor || (
                            <span className="text-gray-400 italic">
                              {t("candidates.table.notAssigned")}
                            </span>
                          )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="space-y-1">
                          <div>
                            <span className="font-medium">
                              {candidate.completedHours || 0}
                            </span>
                            <span className="text-gray-500"> / </span>
                            <span>{candidate.totalHours || 0}</span>
                          </div>
                          <div className="text-xs text-gray-600">
                            <span className="font-medium">
                              {candidate.completedBonusHours || 0}
                            </span>
                            <span className="text-gray-500"> / </span>
                            <span>{candidate.bonusHours || 0}</span>
                            <span className="text-gray-500 ml-1">
                              {t("candidates.table.bonus")}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatDate(candidate.startDate)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <div className="flex items-center justify-center space-x-3">
                          <button
                            onClick={() => handleView(candidate)}
                            className="text-blue-600 hover:text-blue-900 transition-colors"
                            title={t("candidates.actions.view")}
                          >
                            <svg
                              className="w-5 h-5"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                              />
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                              />
                            </svg>
                          </button>
                          {isAdmin && (
                            <>
                              <button
                                onClick={() => handleEdit(candidate)}
                                className="text-indigo-600 hover:text-indigo-900 transition-colors"
                                title={t("candidates.actions.edit")}
                              >
                                <svg
                                  className="w-5 h-5"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                                  />
                                </svg>
                              </button>
                              <button
                                onClick={() => {
                                  setCandidateToDelete(candidate);
                                  setIsDeleteModalOpen(true);
                                }}
                                className="text-red-600 hover:text-red-900 transition-colors"
                                title={t("candidates.actions.delete")}
                              >
                                <svg
                                  className="w-5 h-5"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                  />
                                </svg>
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </>
          )}
        </div>

        {/* Pagination */}
        {!loading && filteredAndSortedCandidates.length > itemsPerPage && (
          <div className="flex items-center justify-center space-x-2 mt-4">
            <button
              onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {t("common.previous")}
            </button>

            <div className="flex items-center space-x-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }

                return (
                  <button
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    className={`px-3 py-2 border rounded-lg text-sm font-medium transition-colors ${
                      currentPage === pageNum
                        ? "bg-black text-white border-black"
                        : "border-gray-300 text-gray-700 bg-white hover:bg-gray-50"
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>

            <button
              onClick={() =>
                setCurrentPage((prev) => Math.min(totalPages, prev + 1))
              }
              disabled={currentPage === totalPages}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {t("common.next")}
            </button>
          </div>
        )}
      </div>

      {/* Add/Edit Candidate Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
            <div className="p-6 border-b border-gray-200 flex-shrink-0">
              <h3 className="text-xl font-bold text-gray-900">
                {editingCandidate
                  ? t("candidates.editCandidate")
                  : t("candidates.addCandidate")}
              </h3>
            </div>

            <form
              onSubmit={handleSubmit}
              className="flex flex-col flex-1 overflow-hidden"
            >
              <div className="p-6 overflow-y-auto flex-1">
                <div className="space-y-6">
                  {/* Personal Information */}
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-4">
                      {t("candidates.form.personalInfo")}
                    </h4>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                      {/* Candidate ID */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t("candidates.form.candidateId")}
                        </label>
                        <input
                          type="text"
                          name="candidateId"
                          value={formData.candidateId}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          placeholder={t(
                            "candidates.form.candidateIdPlaceholder"
                          )}
                          className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10 ${
                            touched.candidateId && errors.candidateId
                              ? "border-red-500"
                              : "border-gray-300"
                          }`}
                        />
                        {touched.candidateId && errors.candidateId && (
                          <p className="mt-1 text-xs text-red-500">
                            {errors.candidateId}
                          </p>
                        )}
                        <p className="mt-1 text-xs text-gray-500">
                          {t("candidates.form.candidateIdHelper")}
                        </p>
                      </div>

                      {/* First Name */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t("candidates.form.firstName")}{" "}
                          <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="firstName"
                          value={formData.firstName}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10 ${
                            touched.firstName && errors.firstName
                              ? "border-red-500"
                              : "border-gray-300"
                          }`}
                        />
                        {touched.firstName && errors.firstName && (
                          <p className="mt-1 text-xs text-red-500">
                            {errors.firstName}
                          </p>
                        )}
                      </div>

                      {/* Last Name */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t("candidates.form.lastName")}{" "}
                          <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="lastName"
                          value={formData.lastName}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10 ${
                            touched.lastName && errors.lastName
                              ? "border-red-500"
                              : "border-gray-300"
                          }`}
                        />
                        {touched.lastName && errors.lastName && (
                          <p className="mt-1 text-xs text-red-500">
                            {errors.lastName}
                          </p>
                        )}
                      </div>

                      {/* Phone Prefix and Number */}
                      <div className="grid grid-cols-3 gap-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t("candidates.form.phonePrefix")}{" "}
                            <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="tel"
                            name="phonePrefix"
                            value={formData.phonePrefix}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            placeholder="+383"
                            className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10 ${
                              touched.phonePrefix && errors.phonePrefix
                                ? "border-red-500"
                                : "border-gray-300"
                            }`}
                          />
                          {touched.phonePrefix && errors.phonePrefix && (
                            <p className="mt-1 text-xs text-red-500">
                              {errors.phonePrefix}
                            </p>
                          )}
                        </div>
                        <div className="col-span-2">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t("candidates.form.phoneNumber")}{" "}
                            <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="tel"
                            name="phoneNumber"
                            value={formData.phoneNumber}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            placeholder={t(
                              "candidates.form.phoneNumberPlaceholder"
                            )}
                            className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10 ${
                              touched.phoneNumber && errors.phoneNumber
                                ? "border-red-500"
                                : "border-gray-300"
                            }`}
                          />
                          {touched.phoneNumber && errors.phoneNumber && (
                            <p className="mt-1 text-xs text-red-500">
                              {errors.phoneNumber}
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Email */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t("candidates.form.email")}
                        </label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10 ${
                            touched.email && errors.email
                              ? "border-red-500"
                              : "border-gray-300"
                          }`}
                        />
                        {touched.email && errors.email && (
                          <p className="mt-1 text-xs text-red-500">
                            {errors.email}
                          </p>
                        )}
                      </div>

                      {/* Username */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t("candidates.form.username")}{" "}
                          <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="username"
                          value={formData.username}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10 ${
                            touched.username && errors.username
                              ? "border-red-500"
                              : "border-gray-300"
                          }`}
                        />
                        {touched.username && errors.username && (
                          <p className="mt-1 text-xs text-red-500">
                            {errors.username}
                          </p>
                        )}
                      </div>

                      {/* Personal Number */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t("candidates.form.personalNumber")}{" "}
                          <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="personalNumber"
                          value={formData.personalNumber}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          maxLength={10}
                          className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10 ${
                            touched.personalNumber && errors.personalNumber
                              ? "border-red-500"
                              : "border-gray-300"
                          }`}
                        />
                        {touched.personalNumber && errors.personalNumber && (
                          <p className="mt-1 text-xs text-red-500">
                            {errors.personalNumber}
                          </p>
                        )}
                      </div>

                      {/* Password (Auto-generated, read-only) */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t("candidates.form.password")}
                        </label>
                        <input
                          type="text"
                          name="password"
                          value={formData.password}
                          readOnly
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600"
                        />
                        <p className="mt-1 text-xs text-gray-500">
                          {t("candidates.form.passwordHelper")}
                        </p>
                      </div>

                      {/* Birthday */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t("candidates.form.birthday")}{" "}
                          <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="birthday"
                          value={formData.birthday}
                          onChange={handleDateChange}
                          onBlur={handleBlur}
                          placeholder="DD/MM/YYYY"
                          maxLength={10}
                          className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10 ${
                            touched.birthday && errors.birthday
                              ? "border-red-500"
                              : "border-gray-300"
                          }`}
                        />
                        {touched.birthday && errors.birthday && (
                          <p className="mt-1 text-xs text-red-500">
                            {errors.birthday}
                          </p>
                        )}
                      </div>

                      {/* Gender */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t("candidates.form.gender")}{" "}
                          <span className="text-red-500">*</span>
                        </label>
                        <select
                          name="gender"
                          value={formData.gender}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          className={`w-full px-4 py-2 h-11 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10 ${
                            touched.gender && errors.gender
                              ? "border-red-500"
                              : "border-gray-300"
                          }`}
                        >
                          <option value="">
                            {t("candidates.form.selectGender")}
                          </option>
                          <option value="male">
                            {t("candidates.form.male")}
                          </option>
                          <option value="female">
                            {t("candidates.form.female")}
                          </option>
                        </select>
                        {touched.gender && errors.gender && (
                          <p className="mt-1 text-xs text-red-500">
                            {errors.gender}
                          </p>
                        )}
                      </div>

                      {/* Address */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t("candidates.form.address")}
                        </label>
                        <input
                          type="text"
                          name="address"
                          value={formData.address}
                          onChange={handleChange}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10"
                        />
                      </div>

                      {/* Parent Name */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t("candidates.form.parentName")}
                        </label>
                        <input
                          type="text"
                          name="parentName"
                          value={formData.parentName}
                          onChange={handleChange}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Registration Details */}
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-4">
                      {t("candidates.form.registrationDetails")}
                    </h4>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                      {/* Start Date */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t("candidates.form.startDate")}{" "}
                          <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="startDate"
                          value={formData.startDate}
                          onChange={handleDateChange}
                          onBlur={handleBlur}
                          placeholder="DD/MM/YYYY"
                          maxLength={10}
                          className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10 ${
                            touched.startDate && errors.startDate
                              ? "border-red-500"
                              : "border-gray-300"
                          }`}
                        />
                        {touched.startDate && errors.startDate && (
                          <p className="mt-1 text-xs text-red-500">
                            {errors.startDate}
                          </p>
                        )}
                      </div>

                      {/* Category */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t("candidates.form.category")}{" "}
                          <span className="text-red-500">*</span>
                        </label>
                        <select
                          name="category"
                          value={formData.category}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          className={`w-full px-4 py-2 h-11 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10 ${
                            touched.category && errors.category
                              ? "border-red-500"
                              : "border-gray-300"
                          }`}
                        >
                          <option value="">
                            {t("candidates.form.selectCategory")}
                          </option>
                          <option value="A">A - Motorcycle</option>
                          <option value="A1">A1 - Light Motorcycle</option>
                          <option value="B">B - Car</option>
                          <option value="C">C - Truck</option>
                          <option value="D">D - Bus</option>
                        </select>
                        {touched.category && errors.category && (
                          <p className="mt-1 text-xs text-red-500">
                            {errors.category}
                          </p>
                        )}
                      </div>

                      {/* Transmission Type */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t("candidates.form.transmissionType")}{" "}
                          <span className="text-red-500">*</span>
                        </label>
                        <select
                          name="transmissionType"
                          value={formData.transmissionType}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          className={`w-full px-4 py-2 h-11 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10 ${
                            touched.transmissionType && errors.transmissionType
                              ? "border-red-500"
                              : "border-gray-300"
                          }`}
                        >
                          <option value="">
                            {t("candidates.form.selectTransmissionType")}
                          </option>
                          <option value="manual">
                            {t(
                              "candidates.form.transmissionTypeOptions.manual"
                            )}
                          </option>
                          <option value="automatic">
                            {t(
                              "candidates.form.transmissionTypeOptions.automatic"
                            )}
                          </option>
                        </select>
                        {touched.transmissionType &&
                          errors.transmissionType && (
                            <p className="mt-1 text-xs text-red-500">
                              {errors.transmissionType}
                            </p>
                          )}
                      </div>

                      {/* Assigned Instructor */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t("candidates.form.assignedInstructor")}
                        </label>
                        <select
                          name="assignedInstructorId"
                          value={formData.assignedInstructorId}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          className={`w-full px-4 py-2 h-11 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10 ${
                            touched.assignedInstructorId &&
                            errors.assignedInstructorId
                              ? "border-red-500"
                              : "border-gray-300"
                          }`}
                        >
                          <option value="">
                            {t("candidates.form.selectInstructor")}
                          </option>
                          {sortedInstructors.map((instructor) => (
                            <option key={instructor.id} value={instructor.id}>
                              {instructor.firstName} {instructor.lastName}
                            </option>
                          ))}
                        </select>
                        {touched.assignedInstructorId &&
                          errors.assignedInstructorId && (
                            <p className="mt-1 text-xs text-red-500">
                              {errors.assignedInstructorId}
                            </p>
                          )}
                      </div>

                      {/* Total Hours */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t("candidates.form.totalHours")}
                        </label>
                        <input
                          type="number"
                          name="totalHours"
                          value={formData.totalHours}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          min="0"
                          className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10 ${
                            touched.totalHours && errors.totalHours
                              ? "border-red-500"
                              : "border-gray-300"
                          }`}
                        />
                        {touched.totalHours && errors.totalHours && (
                          <p className="mt-1 text-xs text-red-500">
                            {errors.totalHours}
                          </p>
                        )}
                      </div>

                      {/* Bonus Hours */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t("candidates.form.bonusHours")}
                        </label>
                        <input
                          type="number"
                          name="bonusHours"
                          value={formData.bonusHours}
                          onChange={handleChange}
                          min="0"
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10"
                        />
                      </div>

                      {/* Completed Hours */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t("candidates.form.completedHours")}
                        </label>
                        <input
                          type="number"
                          name="completedHours"
                          value={formData.completedHours}
                          onChange={handleChange}
                          min="0"
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10"
                        />
                      </div>

                      {/* Completed Bonus Hours */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t("candidates.form.completedBonusHours")}
                        </label>
                        <input
                          type="number"
                          name="completedBonusHours"
                          value={formData.completedBonusHours}
                          onChange={handleChange}
                          min="0"
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10"
                        />
                      </div>

                      {/* Total Amount */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t("candidates.form.totalAmount")}{" "}
                          <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="number"
                          name="totalAmount"
                          value={formData.totalAmount}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          min="0"
                          step="0.01"
                          className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10 ${
                            touched.totalAmount && errors.totalAmount
                              ? "border-red-500"
                              : "border-gray-300"
                          }`}
                        />
                        {touched.totalAmount && errors.totalAmount && (
                          <p className="mt-1 text-xs text-red-500">
                            {errors.totalAmount}
                          </p>
                        )}
                      </div>

                      {/* Paid Amount */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t("candidates.form.paidAmount")}
                        </label>
                        <input
                          type="number"
                          name="paidAmount"
                          value={formData.paidAmount}
                          onChange={handleChange}
                          min="0"
                          step="0.01"
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10"
                        />
                      </div>

                      {/* Health Certificate Date */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t("candidates.form.healthCertificateDate")}
                        </label>
                        <input
                          type="text"
                          name="healthCertificateDate"
                          value={formData.healthCertificateDate}
                          onChange={handleDateChange}
                          placeholder="DD/MM/YYYY"
                          maxLength={10}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10"
                        />
                      </div>

                      {/* Red Cross Date */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t("candidates.form.redCrossDate")}
                        </label>
                        <input
                          type="text"
                          name="redCrossDate"
                          value={formData.redCrossDate}
                          onChange={handleDateChange}
                          placeholder="DD/MM/YYYY"
                          maxLength={10}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10"
                        />
                      </div>

                      {/* Theory Test Date */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t("candidates.form.theoryTestDate")}
                        </label>
                        <input
                          type="text"
                          name="theoryTestDate"
                          value={formData.theoryTestDate}
                          onChange={handleDateChange}
                          placeholder="DD/MM/YYYY"
                          maxLength={10}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10"
                        />
                      </div>

                      {/* Practical Test Date */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t("candidates.form.practicalTestDate")}
                        </label>
                        <input
                          type="text"
                          name="practicalTestDate"
                          value={formData.practicalTestDate}
                          onChange={handleDateChange}
                          placeholder="DD/MM/YYYY"
                          maxLength={10}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10"
                        />
                      </div>

                      {/* Theory Test Status */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t("candidates.form.theoryTestStatus")}
                        </label>
                        <select
                          name="theoryTestStatus"
                          value={formData.theoryTestStatus}
                          onChange={handleChange}
                          className="w-full px-4 py-2 h-11 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10"
                        >
                          <option value="notCompleted">
                            {t("candidates.form.testStatus.notCompleted")}
                          </option>
                          <option value="completed">
                            {t("candidates.form.testStatus.completed")}
                          </option>
                        </select>
                      </div>

                      {/* Practical Test Status */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t("candidates.form.practicalTestStatus")}
                        </label>
                        <select
                          name="practicalTestStatus"
                          value={formData.practicalTestStatus}
                          onChange={handleChange}
                          className="w-full px-4 py-2 h-11 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10"
                        >
                          <option value="notCompleted">
                            {t("candidates.form.testStatus.notCompleted")}
                          </option>
                          <option value="completed">
                            {t("candidates.form.testStatus.completed")}
                          </option>
                        </select>
                      </div>

                      {/* Application AutoShkolla */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t("candidates.form.applicationAutoShkolla")}
                        </label>
                        <select
                          name="applicationAutoShkolla"
                          value={formData.applicationAutoShkolla ? "yes" : "no"}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              applicationAutoShkolla: e.target.value === "yes",
                            })
                          }
                          className="w-full px-4 py-2 h-11 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10"
                        >
                          <option value="no">{t("common.no")}</option>
                          <option value="yes">{t("common.yes")}</option>
                        </select>
                      </div>

                      {/* Literature */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t("candidates.form.literature")}
                        </label>
                        <select
                          name="literature"
                          value={formData.literature ? "yes" : "no"}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              literature: e.target.value === "yes",
                            })
                          }
                          className="w-full px-4 py-2 h-11 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10"
                        >
                          <option value="no">{t("common.no")}</option>
                          <option value="yes">{t("common.yes")}</option>
                        </select>
                      </div>

                      {/* Status */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t("candidates.form.status")}{" "}
                          <span className="text-red-500">*</span>
                        </label>
                        <select
                          name="status"
                          value={formData.status}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          className={`w-full px-4 py-2 h-11 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10 ${
                            touched.status && errors.status
                              ? "border-red-500"
                              : "border-gray-300"
                          }`}
                        >
                          <option value="active">
                            {t("candidates.filters.active")}
                          </option>
                          <option value="completed">
                            {t("candidates.filters.completed")}
                          </option>
                          <option value="onProcess">
                            {t("candidates.filters.onProcess")}
                          </option>
                          <option value="paused">
                            {t("candidates.filters.paused")}
                          </option>
                          <option value="failed">
                            {t("candidates.filters.failed")}
                          </option>
                        </select>
                        {touched.status && errors.status && (
                          <p className="mt-1 text-xs text-red-500">
                            {errors.status}
                          </p>
                        )}
                      </div>

                      {/* Candidate Rating */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t("candidates.form.candidateRating")}
                        </label>
                        <input
                          type="number"
                          name="candidateRating"
                          value={formData.candidateRating}
                          onChange={handleChange}
                          min="1"
                          max="10"
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10"
                        />
                        <p className="mt-1 text-xs text-gray-500">
                          {t("candidates.form.candidateRatingHelper")}
                        </p>
                      </div>

                      {/* Notes */}
                      <div className="lg:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t("candidates.form.notes")}
                        </label>
                        <textarea
                          name="notes"
                          value={formData.notes}
                          onChange={handleChange}
                          rows={3}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Modal Actions */}
              <div className="p-6 border-t border-gray-200 flex-shrink-0">
                <div className="flex flex-col sm:flex-row justify-end gap-3">
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                  >
                    {t("candidates.form.cancel")}
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors font-medium"
                  >
                    {editingCandidate
                      ? t("candidates.form.updateCandidate")
                      : t("candidates.form.createCandidate")}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Candidate Modal */}
      {isViewModalOpen && viewingCandidate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
            <div className="p-6 border-b border-gray-200 flex justify-between items-center flex-shrink-0">
              <h3 className="text-xl font-bold text-gray-900">
                {t("candidates.view.title")}
              </h3>
              <button
                onClick={() => setIsViewModalOpen(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <div className="p-6 space-y-6 overflow-y-auto flex-1">
              {/* Personal Information */}
              <div>
                <h4 className="text-lg font-semibold text-gray-900 mb-3">
                  {t("candidates.form.personalInfo")}
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">
                      {t("candidates.view.candidateId")}
                    </p>
                    <p className="font-semibold text-gray-900">
                      {viewingCandidate.candidateId}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">
                      {t("candidates.view.fullName")}
                    </p>
                    <p className="font-medium">
                      {viewingCandidate.firstName} {viewingCandidate.lastName}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">
                      {t("candidates.view.phoneNumber")}
                    </p>
                    <div className="flex items-center space-x-2">
                      <p className="font-medium">
                        {getFullPhoneNumber(viewingCandidate)}
                      </p>
                      <button
                        onClick={() => openWhatsApp(viewingCandidate)}
                        className="text-green-600 hover:text-green-700 transition-colors"
                        title={t("candidates.actions.whatsapp")}
                      >
                        <svg
                          className="w-5 h-5"
                          fill="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                        </svg>
                      </button>
                      <button
                        onClick={() => openViber(viewingCandidate)}
                        className="text-purple-600 hover:text-purple-700 transition-colors"
                        title={t("candidates.actions.viber")}
                      >
                        <svg
                          className="w-5 h-5"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M13 4C12.4477 4 12 4.44772 12 5C12 5.55228 12.4477 6 13 6C14.2728 6 15.2557 6.41989 15.9179 7.08211C16.5801 7.74433 17 8.72725 17 10C17 10.5523 17.4477 11 18 11C18.5523 11 19 10.5523 19 10C19 8.27275 18.4199 6.75567 17.3321 5.66789C16.2443 4.58011 14.7272 4 13 4Z"
                            fill="currentColor"
                          />
                          <path
                            d="M5.014 8.00613C5.12827 7.1024 6.30277 5.87414 7.23488 6.01043L7.23339 6.00894C8.01251 6.15699 8.65217 7.32965 9.07373 8.10246C9.14298 8.22942 9.20635 8.34559 9.26349 8.44465C9.55041 8.95402 9.3641 9.4701 9.09655 9.68787C9.06561 9.7128 9.03317 9.73855 8.9998 9.76504C8.64376 10.0477 8.18114 10.4149 8.28943 10.7834C8.5 11.5 11 14 12.2296 14.7107C12.6061 14.9283 12.8988 14.5057 13.1495 14.1438C13.2087 14.0583 13.2656 13.9762 13.3207 13.9067C13.5301 13.6271 14.0466 13.46 14.5548 13.736C15.3138 14.178 16.0288 14.6917 16.69 15.27C17.0202 15.546 17.0977 15.9539 16.8689 16.385C16.4659 17.1443 15.3003 18.1456 14.4542 17.9421C12.9764 17.5868 7 15.27 5.08033 8.55801C4.97981 8.26236 4.99645 8.13792 5.01088 8.02991L5.014 8.00613Z"
                            fill="currentColor"
                          />
                          <path
                            d="M13 7C12.4477 7 12 7.44772 12 8C12 8.55228 12.4477 9 13 9C13.1748 9 13.4332 9.09745 13.6679 9.33211C13.9025 9.56676 14 9.82523 14 10C14 10.5523 14.4477 11 15 11C15.5523 11 16 10.5523 16 10C16 9.17477 15.5975 8.43324 15.0821 7.91789C14.5668 7.40255 13.8252 7 13 7Z"
                            fill="currentColor"
                          />
                          <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M7.51742 23.8312C7.54587 23.8469 7.57508 23.8612 7.60492 23.874C8.14762 24.1074 8.81755 23.5863 10.1574 22.5442L11.5 21.5C14.1884 21.589 16.514 21.2362 18.312 20.6071C20.3227 19.9035 21.9036 18.3226 22.6072 16.3119C23.5768 13.541 23.5768 8.45883 22.6072 5.68794C21.9036 3.67722 20.3227 2.0963 18.312 1.39271C15.1103 0.272407 8.82999 0.293306 5.68806 1.39271C3.67733 2.0963 2.09642 3.67722 1.39283 5.68794C0.423255 8.45883 0.423255 13.541 1.39283 16.3119C2.09642 18.3226 3.67733 19.9035 5.68806 20.6071C6.08252 20.7451 6.52371 20.8965 7 21C7 22.6974 7 23.5461 7.51742 23.8312ZM9 20.9107V19.7909C9 19.5557 8.836 19.3524 8.60597 19.3032C7.84407 19.1403 7.08676 18.9776 6.34862 18.7193C4.91238 18.2168 3.78316 17.0875 3.2806 15.6513C2.89871 14.5599 2.66565 12.8453 2.66565 10.9999C2.66565 9.15453 2.89871 7.43987 3.2806 6.3485C3.78316 4.91227 4.91238 3.78304 6.34862 3.28048C7.61625 2.83692 9.71713 2.56282 11.9798 2.56032C14.2422 2.55782 16.3561 2.82723 17.6514 3.28048C19.0876 3.78304 20.2169 4.91227 20.7194 6.3485C21.1013 7.43987 21.3344 9.15453 21.3344 10.9999C21.3344 12.8453 21.1013 14.5599 20.7194 15.6513C20.2169 17.0875 19.0876 18.2168 17.6514 18.7193C15.5197 19.4652 13.259 19.549 11.0239 19.4828C10.9071 19.4794 10.7926 19.5165 10.7004 19.5882L9 20.9107Z"
                            fill="currentColor"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">
                      {t("candidates.view.email")}
                    </p>
                    <p className="font-medium">
                      {viewingCandidate.email ||
                        t("candidates.table.notAssigned")}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">
                      {t("candidates.view.username")}
                    </p>
                    <p className="font-medium">{viewingCandidate.username}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">
                      {t("candidates.view.personalNumber")}
                    </p>
                    <p className="font-medium">
                      {viewingCandidate.personalNumber}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">
                      {t("candidates.view.birthday")}
                    </p>
                    <p className="font-medium">
                      {formatDate(viewingCandidate.birthday)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">
                      {t("candidates.view.gender")}
                    </p>
                    <p className="font-medium capitalize">
                      {viewingCandidate.gender}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">
                      {t("candidates.view.address")}
                    </p>
                    <p className="font-medium">
                      {viewingCandidate.address ||
                        t("candidates.table.notAssigned")}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">
                      {t("candidates.view.parentName")}
                    </p>
                    <p className="font-medium">
                      {viewingCandidate.parentName ||
                        t("candidates.table.notAssigned")}
                    </p>
                  </div>
                </div>
              </div>

              {/* Registration Details */}
              <div>
                <h4 className="text-lg font-semibold text-gray-900 mb-3">
                  {t("candidates.form.registrationDetails")}
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">
                      {t("candidates.view.startDate")}
                    </p>
                    <p className="font-medium">
                      {formatDate(viewingCandidate.startDate)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">
                      {t("candidates.view.category")}
                    </p>
                    <p className="font-medium">
                      {viewingCandidate.category ||
                        t("candidates.table.notAssigned")}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">
                      {t("candidates.view.transmissionType")}
                    </p>
                    <p className="font-medium">
                      {viewingCandidate.transmissionType
                        ? t(
                            `candidates.form.transmissionTypeOptions.${viewingCandidate.transmissionType}`
                          )
                        : t("candidates.table.notAssigned")}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">
                      {t("candidates.view.assignedInstructor")}
                    </p>
                    <p className="font-medium">
                      {getInstructorNameById(
                        viewingCandidate.assignedInstructorId
                      ) ||
                        viewingCandidate.assignedInstructor ||
                        t("candidates.table.notAssigned")}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">
                      {t("candidates.view.totalHours")}
                    </p>
                    <p className="font-medium">
                      {viewingCandidate.totalHours || 0}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">
                      {t("candidates.view.bonusHours")}
                    </p>
                    <p className="font-medium">
                      {viewingCandidate.bonusHours || 0}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">
                      {t("candidates.view.completedHours")}
                    </p>
                    <p className="font-medium">
                      {viewingCandidate.completedHours || 0}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">
                      {t("candidates.view.completedBonusHours")}
                    </p>
                    <p className="font-medium">
                      {viewingCandidate.completedBonusHours || 0}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">
                      {t("candidates.view.totalAmount")}
                    </p>
                    <p className="font-medium">
                      €{viewingCandidate.totalAmount || 0}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">
                      {t("candidates.view.paidAmount")}
                    </p>
                    <p className="font-medium">
                      €{viewingCandidate.paidAmount || 0}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">
                      {t("candidates.view.healthCertificateDate")}
                    </p>
                    <p className="font-medium">
                      {viewingCandidate.healthCertificateDate
                        ? formatDate(viewingCandidate.healthCertificateDate)
                        : t("candidates.table.notAssigned")}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">
                      {t("candidates.view.redCrossDate")}
                    </p>
                    <p className="font-medium">
                      {viewingCandidate.redCrossDate
                        ? formatDate(viewingCandidate.redCrossDate)
                        : t("candidates.table.notAssigned")}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">
                      {t("candidates.view.theoryTestDate")}
                    </p>
                    <p className="font-medium">
                      {viewingCandidate.theoryTestDate
                        ? formatDate(viewingCandidate.theoryTestDate)
                        : t("candidates.table.notAssigned")}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">
                      {t("candidates.view.practicalTestDate")}
                    </p>
                    <p className="font-medium">
                      {viewingCandidate.practicalTestDate
                        ? formatDate(viewingCandidate.practicalTestDate)
                        : t("candidates.table.notAssigned")}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">
                      {t("candidates.view.theoryTestStatus")}
                    </p>
                    <p className="font-medium">
                      {viewingCandidate.theoryTestStatus
                        ? t(
                            `candidates.form.testStatus.${viewingCandidate.theoryTestStatus}`
                          )
                        : t("candidates.table.notAssigned")}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">
                      {t("candidates.view.practicalTestStatus")}
                    </p>
                    <p className="font-medium">
                      {viewingCandidate.practicalTestStatus
                        ? t(
                            `candidates.form.testStatus.${viewingCandidate.practicalTestStatus}`
                          )
                        : t("candidates.table.notAssigned")}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">
                      {t("candidates.view.applicationAutoShkolla")}
                    </p>
                    <p className="font-medium">
                      {viewingCandidate.applicationAutoShkolla
                        ? t("common.yes")
                        : t("common.no")}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">
                      {t("candidates.view.literature")}
                    </p>
                    <p className="font-medium">
                      {viewingCandidate.literature
                        ? t("common.yes")
                        : t("common.no")}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">
                      {t("candidates.view.status")}
                    </p>
                    <span
                      className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        viewingCandidate.status === "active"
                          ? "bg-green-100 text-green-800"
                          : viewingCandidate.status === "completed"
                          ? "bg-blue-100 text-blue-800"
                          : viewingCandidate.status === "onProcess"
                          ? "bg-purple-100 text-purple-800"
                          : viewingCandidate.status === "paused"
                          ? "bg-yellow-100 text-yellow-800"
                          : "bg-red-100 text-red-800"
                      }`}
                    >
                      {t(`candidates.filters.${viewingCandidate.status}`)}
                    </span>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">
                      {t("candidates.view.candidateRating")}
                    </p>
                    <p className="font-medium">
                      {viewingCandidate.candidateRating ||
                        t("candidates.table.notAssigned")}
                    </p>
                  </div>
                  {viewingCandidate.notes && (
                    <div className="md:col-span-2">
                      <p className="text-sm text-gray-500">
                        {t("candidates.view.notes")}
                      </p>
                      <p className="font-medium">{viewingCandidate.notes}</p>
                    </div>
                  )}
                </div>

                {/* Instructor History */}
                {viewingCandidate.instructorHistory &&
                  viewingCandidate.instructorHistory.length > 0 && (
                    <div className="mt-6 pt-6 border-t border-gray-200">
                      <h4 className="text-sm font-semibold text-gray-900 mb-4">
                        {t("candidates.view.instructorHistory")}
                      </h4>
                      <div className="space-y-3">
                        {viewingCandidate.instructorHistory.map(
                          (history, index) => (
                            <div
                              key={index}
                              className="bg-gray-50 rounded-lg p-4 border border-gray-200"
                            >
                              <div className="flex justify-between items-start mb-2">
                                <div>
                                  <p className="font-medium text-gray-900">
                                    {history.instructorName ||
                                      t("candidates.view.unknownInstructor")}
                                  </p>
                                  <p className="text-xs text-gray-500 mt-1">
                                    {t("candidates.view.changedOn")}:{" "}
                                    {history.dateChanged
                                      ? formatDate(history.dateChanged)
                                      : "-"}
                                  </p>
                                </div>
                                <div className="text-right">
                                  <p className="text-sm font-semibold text-gray-900">
                                    {history.totalHours || 0}h
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    {history.normalHours || 0}h{" "}
                                    {t("candidates.view.normal")} +{" "}
                                    {history.bonusHours || 0}h{" "}
                                    {t("candidates.view.bonus")}
                                  </p>
                                </div>
                              </div>
                            </div>
                          )
                        )}
                      </div>
                    </div>
                  )}
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 flex justify-end flex-shrink-0">
              <button
                onClick={() => setIsViewModalOpen(false)}
                className="px-6 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors font-medium"
              >
                {t("candidates.view.close")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && candidateToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">
              {t("candidates.delete.title")}
            </h3>
            <p className="text-gray-600 mb-6">
              {t("candidates.delete.message")}{" "}
              <span className="font-semibold text-gray-900">
                {candidateToDelete.firstName} {candidateToDelete.lastName}
              </span>{" "}
              ({candidateToDelete.candidateId})
              {t("candidates.delete.cannotUndo")}
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setIsDeleteModalOpen(false);
                  setCandidateToDelete(null);
                }}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
              >
                {t("candidates.delete.cancel")}
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
              >
                {t("candidates.delete.delete")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {toast.show && (
        <div className="fixed bottom-4 right-4 z-50 animate-slide-up">
          <div
            className={`px-6 py-3 rounded-lg shadow-lg flex items-center space-x-3 ${
              toast.type === "success"
                ? "bg-green-600 text-white"
                : "bg-red-600 text-white"
            }`}
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              {toast.type === "success" ? (
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              ) : (
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              )}
            </svg>
            <span className="font-medium">{toast.message}</span>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}

export default Candidates;
